import type {CSSProperties} from 'react';

import {
  ACTIVE_TONE,
  DEACTIVATED_ROW_OPACITY,
  INACTIVE_TONE,
  type StatusTone as Tone,
} from '@/lib/status-tone';

/**
 * The active / inactive vocabulary, for records that are switched off rather
 * than deleted.
 *
 * The display half (`Activity`, `ACTIVITY_*`, `activityOf`, and `ActivityPill` /
 * `ActivityChips` over it) is read by the sites and clients registries.
 * `selectableActive` is the picker half, and has four callers: the client,
 * project-type and manager directories, and the advance/job site pickers.
 *
 * One owner, because the third copy was one too many. Sites and clients had each
 * grown their own `*_ACTIVITY_ORDER` / `_LABEL` / `_TONE` triple, their own
 * `is_active` → status projection and their own pill component, spelling the
 * same two states two different ways: the clients copy tested `is_active`
 * truthily where the sites copy tested `=== false`, and re-inlined the literal
 * `rgba` tones that `@/lib/status-tone` exists to keep theme-aware. Four hooks
 * had also hand-rolled `.filter((r) => r.is_active)`, each losing the
 * missing-field guard below.
 *
 * `ProjectRow` deliberately stays out: it renders a pill only when a project is
 * INACTIVE, so it reads `INACTIVE_TONE` directly rather than this two-state
 * vocabulary.
 *
 * Lives in `@/lib` rather than a module so every registry, the shared row
 * components and the app chrome can read it without a cross-module import.
 */
export type Activity = 'active' | 'inactive';

/** Display order for activity filter chips. Active first — it is the norm. */
export const ACTIVITY_ORDER: readonly Activity[] = ['active', 'inactive'];

export const ACTIVITY_LABEL: Record<Activity, string> = {
  active: 'Active',
  inactive: 'Inactive',
};

export const ACTIVITY_TONE: Record<Activity, Tone> = {
  active: ACTIVE_TONE,
  inactive: INACTIVE_TONE,
};

/**
 * A record's activity, read defensively.
 *
 * Only an explicit `false` counts as inactive. Every pre-existing row is active
 * and an absent field means active too, so a truthiness check would grey out a
 * whole registry if this Web ever ran against an API without the column (deploy
 * order, or a stale spec): the loudest possible failure for the least reason.
 *
 * This is the ONLY place that defensiveness belongs. It reads a wire payload,
 * where a missing field is a real possibility — which is why the parameter type
 * still declares `is_active` REQUIRED. Internal prop shapes that flow in here
 * are then checked by the type checker rather than by a silent default, so a
 * projection that drops the field is a compile error, not a silent "active".
 */
export function activityOf(record: {is_active: boolean}): Activity {
  return record.is_active === false ? 'inactive' : 'active';
}

/**
 * A registry row's style for a record that can be switched off: dimmed, not
 * hidden (DERP-242), so a project, site or any other record reads the same.
 */
export function inactiveRowStyle(record: {is_active: boolean}): CSSProperties | undefined {
  return activityOf(record) === 'inactive' ? {opacity: DEACTIVATED_ROW_OPACITY} : undefined;
}

/**
 * The rows a picker may offer for NEW work.
 *
 * Inactive rows drop out: deactivating one is how the master-data cleanup
 * retires a wrong or finished record, and leaving it in the dropdown invites the
 * next user to attach new work to it.
 *
 * `keepIds` is REQUIRED, with no default, because omitting it is a bug at most
 * call sites: pass the ids the record ALREADY points at. A line saved against a
 * row that was deactivated afterwards must still show it, or reopening the
 * record silently drops linkage the user never touched. Pass `[]` explicitly
 * where a picker genuinely offers only new work. Filtering is display-only;
 * nothing here decides what gets submitted.
 *
 * `is_active` is REQUIRED on the element type. An optional flag would let a
 * caller hand over a projected shape that dropped the field —
 * `rows.map((r) => ({id: r.id, name: r.name}))` — and `activityOf` would read
 * that as active, turning this filter into a silent no-op with no type error.
 *
 * The activity test goes through `activityOf` rather than re-spelling
 * `is_active !== false` here: that guard has one owner, and two spellings of it
 * are two things to keep in agreement.
 */
export function selectableActive<T extends {id: string; is_active: boolean}>(
  rows: ReadonlyArray<T>,
  keepIds: Iterable<string>,
): ReadonlyArray<T> {
  const kept = new Set(keepIds);
  return rows.filter((row) => activityOf(row) === 'active' || kept.has(row.id));
}
